import type { ChangeRequest } from '../model/schema';
import { payloadBag, requestBaselineOf } from './entity-bag';

/**
 * Strict request classification for the review UI. Every screen branches on
 * these two axes only — never on raw `operation` / `status` strings.
 *
 * OPERATION (what the merchant asks for), from `v1ChangeRequest.operation`:
 * - `create` — `"publish"`: a merchant's "ready" on a draft, i.e. the
 *   entity's publication review. Legacy rows say `"create"`, and lifecycle
 *   `"transition"` rows that hand the entity to the storefront
 *   (`ready_for_life` / `life`) are the same publication review;
 * - `update` — `"update"`: an edit of an entity that already exists;
 * - `delete` — `"delete"`: removal of an existing entity.
 * An unknown operation falls back on the spec's own invariant: only a
 * create has no `baseline`.
 *
 * DECISION (where the request stands), from `v1ChangeRequest.status`:
 * - `pending` — awaits a moderator; the only actionable state;
 * - `approved` / `rejected` — decided by a moderator (or the system);
 * - `closed` — `stale` (the entity moved on under it) or `withdrawn` (the
 *   merchant pulled it, or a newer version superseded it): settled without
 *   a verdict. Unknown statuses read as closed — never as actionable.
 */
export type ModerationOperation = 'create' | 'update' | 'delete';
export type ModerationDecision = 'pending' | 'approved' | 'rejected' | 'closed';

function normalized(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/** `ready_for_life` / `life`, tolerating prefixed enum spellings. */
function isPublicationLifecycle(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const lifecycle = normalized(value);
  return (
    lifecycle === 'life' ||
    lifecycle.endsWith('_life') ||
    lifecycle === 'ready_for_life' ||
    lifecycle.endsWith('_ready_for_life')
  );
}

export function requestOperation(
  request: Pick<ChangeRequest, 'operation' | 'payload' | 'baseline'>,
): ModerationOperation {
  const operation = normalized(request.operation);
  switch (operation) {
    case 'publish':
    case 'create':
      return 'create';
    case 'update':
      return 'update';
    case 'delete':
      return 'delete';
    case 'transition':
      return isPublicationLifecycle(payloadBag(request).lifecycle_status)
        ? 'create'
        : 'update';
    default:
      return requestBaselineOf(request) === null ? 'create' : 'update';
  }
}

export function requestDecision(
  request: Pick<ChangeRequest, 'status'>,
): ModerationDecision {
  switch (normalized(request.status)) {
    case 'pending':
      return 'pending';
    case 'approved':
      return 'approved';
    case 'rejected':
      return 'rejected';
    default:
      return 'closed';
  }
}

/** Whether the request still awaits a moderator decision. */
export function isRequestPending(request: Pick<ChangeRequest, 'status'>): boolean {
  return requestDecision(request) === 'pending';
}

/** The request was amended by a reviewer before it applied. */
export function isRequestAmended(
  request: Pick<ChangeRequest, 'original_payload' | 'amended_at'>,
): boolean {
  const original: unknown = request.original_payload;
  return (
    (original !== null && typeof original === 'object' && !Array.isArray(original)) ||
    (request.amended_at?.trim() ?? '') !== ''
  );
}

/** The request settled with nobody reviewing it (system actor). */
export function isSystemSettled(request: Pick<ChangeRequest, 'reviewer'>): boolean {
  return normalized(request.reviewer?.kind) === 'system';
}

/** Withdrawn because a newer version of the same entity replaced it. */
export function isSuperseded(
  request: Pick<ChangeRequest, 'reviewer' | 'status'>,
): boolean {
  return (
    normalized(request.status) === 'withdrawn' &&
    normalized(request.reviewer?.id) === 'system:superseded'
  );
}
